import {
  createSlice,
  PayloadAction,
  Draft,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import type { ApiResponse } from 'commons/modules/generic-response';
import type { RootState } from './configureStore';
import type { ReportStoreType } from './types/report-store';

// ======================================================
// 🔹 Helper para normalizar datos (si se requiere)
// ======================================================
export const assignBy = (key: string) => {
  return (data: Record<string, any>, item: any) => {
    data[item[key]] = item;
    return data;
  };
};

// ======================================================
// 🔹 Generador de stores reutilizables
// ======================================================
export const createReportSlice = <TData = any>(
  sliceName: string,
  initialFilters: Record<string, string> = {},
  extend?: (builder: ActionReducerMapBuilder<ReportStoreType<TData>>) => void,
  initialPageSize = 50,
) => {
  const initialState: ReportStoreType<TData> = {
    data: [],
    total: 0,
    page: 1,
    pageSize: initialPageSize,

    filters: { ...initialFilters },

    cleanFilters: false,
    openFilter: Object.keys(initialFilters).reduce<Record<string, boolean>>(
      (acc, key) => ({ ...acc, [key]: false }),
      {},
    ),
    dataQuery: Object.keys(initialFilters).reduce<Record<string, any>>(
      (acc, key) => ({ ...acc, [key]: [] }),
      {},
    ),
    showTooltip: true,
    isSubmitting: false,
    loading: false,
    complete: false,
    filterOpened: true,
    filtersObject: false,
    dates: { startDate: '', endDate: '' },
  };

  const slice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      // ======================================================
      // UI control
      // ======================================================
      toggleFilterSidebar(state) {
        state.filterOpened = !state.filterOpened;
      },

      setOpenFilter(state, action: PayloadAction<{ name: string }>) {
        const filterName = action.payload.name;
        state.openFilter[filterName] = !state.openFilter[filterName];
      },

      closeAllFilters(state) {
        Object.keys(state.openFilter).forEach((key) => {
          state.openFilter[key] = false;
        });
        state.isSubmitting = false;
      },

      setOpenTooltip(state, action: PayloadAction<boolean>) {
        state.showTooltip = action.payload;
      },

      // ======================================================
      // Paginación y datos
      // ======================================================
      setData(state, action: PayloadAction<ApiResponse<TData>>) {
        state.data = action.payload.data as unknown as Draft<TData[]>;
        state.total = action.payload.pagination?.total || 0;
      },

      setPage(state, action: PayloadAction<number>) {
        state.page = action.payload;
      },

      setPageSize(state, action: PayloadAction<number>) {
        state.pageSize = action.payload;
      },

      resetPagination(state) {
        state.page = 1;
        state.pageSize = initialPageSize;
      },

      // ======================================================
      // Filtros dinámicos
      // ======================================================
      setFilterValues(
        state,
        action: PayloadAction<{
          values: any[];
          filterType: string;
          optionShow: string;
          returnParam: string;
        }>,
      ) {
        const {
          values,
          filterType: filterKey,
          optionShow,
          returnParam: retParam,
        } = action.payload;

        const getValues = (
          data: any[],
          param = '',
          type: string,
          rParam = '',
        ): string => {
          if (_.isEmpty(data)) return '';
          const arrayQuery = data.map((v) =>
            rParam ? v[rParam] : (v[param] ?? v),
          );
          return arrayQuery.join(',');
        };

        state.filters[filterKey] = getValues(
          values,
          optionShow,
          filterKey,
          retParam,
        );
      },

      setFilterValue(
        state,
        action: PayloadAction<{ filterType: string; value: string }>,
      ) {
        const { filterType, value } = action.payload;
        state.filters[filterType] = value;
      },

      setDataQuery(state, action: PayloadAction<{ name: string; value: any }>) {
        const queryName = action.payload.name;
        state.dataQuery[queryName] = action.payload.value;
      },

      cleanInputs(state, action: PayloadAction<boolean>) {
        Object.keys(state.filters).forEach((key) => {
          state.filters[key] = '';
          state.dataQuery[key] = [];
        });
        state.cleanFilters = action.payload;
      },

      cleanAll(state) {
        // Preservar las fechas al limpiar
        const { dates } = state;
        return { ...initialState, dates };
      },

      // ======================================================
      //   Estado de ejecución
      // ======================================================
      setSendFilters(state, action: PayloadAction<boolean>) {
        state.filtersObject = action.payload;
      },

      setComplete(state, action: PayloadAction<boolean>) {
        state.complete = action.payload;
      },

      setLoading(state, action: PayloadAction<boolean>) {
        state.loading = action.payload;
      },

      setSubmit(state, action: PayloadAction<boolean>) {
        state.isSubmitting = action.payload;
      },

      // ======================================================
      // Manejo de fechas
      // ======================================================
      setStartDate(state, action: PayloadAction<string>) {
        state.dates.startDate = action.payload;
      },

      setEndDate(state, action: PayloadAction<string>) {
        state.dates.endDate = action.payload;
      },
      cleanDate(state) {
        state.dates = initialState.dates;
      },

      // ======================================================
      // Helper: aplicar filtros (común en muchos componentes)
      // ======================================================
      applyFilters(
        state,
        action: PayloadAction<{ resetPagination?: boolean } | undefined>,
      ) {
        const resetPagination = action.payload?.resetPagination ?? true;

        state.isSubmitting = true;
        if (resetPagination) {
          state.page = 1;
          state.pageSize = initialPageSize;
        }
        state.filtersObject = true;
        state.showTooltip = false;
        state.data = [];
      },
    },
    extraReducers: (builder) => {
      if (extend) extend(builder);
    },
  });

  return slice;
};

// ======================================================
// 🔹 Selectores genéricos (por nombre del slice)
// ======================================================
export const createReportSelectors = (
  sliceName: keyof RootState['entities'],
) => {
  const base = (state: RootState) =>
    state.entities[sliceName] as unknown as ReportStoreType;

  return {
    getData: (state: RootState) => base(state).data,
    getFilters: (state: RootState) => base(state).filters,
    getDataQuery: (state: RootState) => base(state).dataQuery,
    getOpenFilter: (state: RootState) => base(state).openFilter,
    getCleanFilters: (state: RootState) => base(state).cleanFilters,
    getLoading: (state: RootState) => base(state).loading,
    getSubmit: (state: RootState) => base(state).isSubmitting,
    getTotal: (state: RootState) => base(state).total,
    getPage: (state: RootState) => base(state).page,
    getPageSize: (state: RootState) => base(state).pageSize,
    getShowTooltip: (state: RootState) => base(state).showTooltip,
    getFilterOpened: (state: RootState) => base(state).filterOpened,
    getFiltersObject: (state: RootState) => base(state).filtersObject,
    getDates: (state: RootState) => base(state).dates,
  };
};
